import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { MapPinIcon } from '../utils/IconWrapper';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1558383817-8fbd5164d689?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const AttractionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const AttractionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryImage = styled.div`
  height: 300px;
  overflow: hidden;
  border-radius: 8px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 2rem 0;
`;

const FeatureCard = styled.div`
  background-color: var(--bg-secondary);
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const FeatureDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
`;

const AttractionDetails: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Attrazioni | J-O-H' 
            : 'Attractions | J-O-H'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri le meravigliose attrazioni e luoghi di interesse vicino alle nostre proprietà in tutta Italia.'
            : 'Discover the wonderful attractions and places of interest near our properties throughout Italy.'} 
        />
        <meta 
          name="keywords" 
          content={[
            'J-O-H',
            'Attrazioni',
            'Toscana',
            'Liguria',
            'Italia',
            'Turismo',
            'Viaggi',
            'Viaggio',
            'Vacanze',
            'Vacanza',
            'Cultural',
            'Cultural Experience',
            'Enogastronomia',
            'Food and Wine',
            'Paesaggi Naturali',
            'Natural Landscapes',
            'Città d\'Arte',
            'Art Cities',
            'Scopri la Bellezza della Toscana',
            'Discover the Beauty of Tuscany',
            'Il Fascino della Liguria',
            'The Charm of Liguria',
            'Cinque Terre',
            'Portofino',
            'Genova',
            'Scopri le meravigliose attrazioni e luoghi di interesse vicino alle nostre proprietà in tutta Italia.',
            'Discover the wonderful attractions and places of interest near our properties throughout Italy.'
          ].join(', ')}
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Attrazioni' : 'Attractions'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Esplora luoghi incantevoli e scopri esperienze uniche vicino alle nostre proprietà.'
              : 'Explore enchanting places and discover unique experiences near our properties.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Pianifica la Tua Visita' : 'Plan Your Visit'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <SectionTitle>
            {language === 'it' ? 'Scopri la Bellezza dell\'Italia' : 'Discover the Beauty of Italy'}
          </SectionTitle>
          <SectionDescription>
            {language === 'it' 
              ? 'L\'Italia è una regione ricca di storia, cultura e paesaggi mozzafiato. Dalle famose città d\'arte come Firenze e Roma alle pittoresche coste e colline, ogni angolo offre esperienze indimenticabili per i nostri ospiti.'
              : 'Italy is a country rich in history, culture, and breathtaking landscapes. From famous art cities like Florence and Rome to picturesque coasts and hills, every corner offers unforgettable experiences for our guests.'}
          </SectionDescription>
          
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>🏛️</FeatureIcon>
              <FeatureTitle>
                {language === 'it' ? 'Patrimonio Artistico' : 'Artistic Heritage'}
              </FeatureTitle>
              <FeatureDescription>
                {language === 'it' 
                  ? 'Musei, gallerie d\'arte e monumenti storici che custodiscono secoli di capolavori artistici.'
                  : 'Museums, art galleries, and historical monuments that preserve centuries of artistic masterpieces.'}
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>🍷</FeatureIcon>
              <FeatureTitle>
                {language === 'it' ? 'Eccellenza Enogastronomica' : 'Gastronomic Excellence'}
              </FeatureTitle>
              <FeatureDescription>
                {language === 'it' 
                  ? 'Scopri l\'eccellenza enogastronomica italiana con degustazioni di vini nelle cantine del Chianti, lezioni di cucina tradizionale e visite ai mercati locali.'
                  : 'Discover Italian gastronomic excellence with wine tastings in Chianti cellars, traditional cooking lessons, and visits to local markets.'}
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard>
              <FeatureIcon>🌿</FeatureIcon>
              <FeatureTitle>
                {language === 'it' ? 'Paesaggi Naturali' : 'Natural Landscapes'}
              </FeatureTitle>
              <FeatureDescription>
                {language === 'it' 
                  ? 'Dalle colline del Chianti alle coste della Riviera, ogni paesaggio racconta una storia unica.'
                  : 'From the Chianti hills to the Riviera coasts, every landscape tells a unique story.'}
              </FeatureDescription>
            </FeatureCard>
          </FeatureGrid>
          
          <SectionTitle>
            {language === 'it' ? 'Il Fascino delle Coste Italiane' : 'The Charm of Italian Coasts'}
          </SectionTitle>
          <SectionDescription>
            {language === 'it' 
              ? 'Le coste italiane, con le loro spiagge spettacolari e i pittoreschi borghi colorati, offrono un\'esperienza unica tra mare e tradizione. Le Cinque Terre, Portofino e molte altre meraviglie sono solo alcune delle destinazioni da scoprire.'
              : 'The Italian coasts, with their spectacular beaches and picturesque colorful villages, offer a unique experience between the sea and tradition. The Cinque Terre, Portofino and many other wonders are just some of the destinations to discover.'}
          </SectionDescription>
          
          <ImageGallery>
            <GalleryImage>
              <img 
                src="https://relaisdelmaro.it/wp-content/uploads/2023/06/5-5.jpg" 
                alt={language === 'it' ? 'Cinque Terre' : 'Cinque Terre'} 
              />
            </GalleryImage>
            <GalleryImage>
              <img 
                src="https://www.italia.it/content/dam/tdh/it/destinations/liguria/genova/portofino/media/2480X1000_portofino_destination.jpg" 
                alt={language === 'it' ? 'Portofino' : 'Portofino'} 
              />
            </GalleryImage>
          </ImageGallery>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Pronto a Esplorare?' : 'Ready to Explore?'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci per pianificare la tua visita e scoprire itinerari personalizzati in base ai tuoi interessi e al tempo a disposizione.'
                : 'Contact us to plan your visit and discover personalized itineraries based on your interests and available time.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default AttractionDetails; 